/**
 * Venner · Raspadita.
 *
 * Captura de correos con mecánica de «raspa y gana». Reemplaza a la ruleta de Klaviyo
 * (Klaviyo no tiene tarjeta de raspar: su única forma gamificada nativa es Spin to Win).
 *
 * Cómo habla con Klaviyo:
 *
 *   · El correo se suscribe con POST /client/subscriptions, que es el endpoint
 *     público del Client API: autentica con la llave PÚBLICA (company_id) y responde
 *     202. Nunca una llave privada — cualquier visitante puede leer este archivo.
 *   · El cumpleaños se guarda con POST /client/profiles, que actualiza propiedades
 *     sin tocar el consentimiento ya otorgado.
 *   · Si el embed de Klaviyo está activo (lo está), también se hace `identify` para
 *     amarrar la sesión del navegador al perfil y no perder la atribución del
 *     carrito abandonado.
 *
 * El premio se sortea en el cliente. Eso significa que alguien con la consola abierta
 * puede ver las probabilidades y forzar el premio bueno: la defensa real son los
 * límites de uso de cada código en Shopify, no el secreto del sorteo.
 */

(() => {
  const STORAGE = 'venner:scratch:v1';

  // Rutas donde nunca interrumpimos: ya está comprando o autenticándose.
  const BLOCKED_PATHS = /^\/(checkouts?|cart\/|account|tools|apps|challenge|password|\d+\/(checkouts|orders))/;

  const BOT =
    /bot|crawl|spider|slurp|bingpreview|facebookexternalhit|whatsapp|telegram|preview|lighthouse|headless|pingdom|gtmetrix|semrush|ahrefs/i;

  const KLAVIYO_HOST = 'https://a.klaviyo.com';

  // Cuánto se le espera al aviso de país antes de darlo por perdido, y el respiro que se
  // le deja a su hoja para salir de la pantalla antes de abrir la raspadita.
  const GEO_WAIT = 8000;
  const GEO_GRACE = 600;

  function read() {
    try {
      const raw = localStorage.getItem(STORAGE);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  function write(value) {
    try {
      localStorage.setItem(STORAGE, JSON.stringify(value));
    } catch {
      /* Modo privado o almacenamiento bloqueado: el popup sigue funcionando,
         solo deja de recordar que ya se mostró. */
    }
  }

  const days = (n) => n * 24 * 60 * 60 * 1000;

  function fill(template, values) {
    return String(template || '').replace(/\{(PREMIO|CODIGO)\}/g, (match, key) => {
      return key === 'PREMIO' ? values.prize : values.code;
    });
  }

  /**
   * Sorteo por pesos. No necesitan sumar 100: se reparten en proporción, así que
   * agregar o quitar un premio no obliga a recalcular los demás.
   */
  function pickPrize(prizes) {
    const total = prizes.reduce((sum, prize) => sum + Math.max(0, prize.weight || 0), 0);
    if (total <= 0) return prizes[prizes.length - 1];

    let ticket = Math.random() * total;
    for (const prize of prizes) {
      ticket -= Math.max(0, prize.weight || 0);
      if (ticket <= 0) return prize;
    }
    return prizes[prizes.length - 1];
  }

  /**
   * Medición. El mismo evento sale por tres puertas porque cada una llega a un
   * lado distinto, y ninguna sola alcanza:
   *
   *   · `Shopify.analytics.publish` — el puente oficial al sandbox de Web Pixels.
   *     Es el ÚNICO camino hasta GA4 en esta tienda: GA4 vive dentro del sandbox,
   *     no en la página, y por eso un `gtag` suelto nunca le llega (comprobado
   *     contra el live: el evento salía a Google Ads y a GA4 no).
   *   · `dataLayer` + `gtag` — ahí sí escucha Google Ads, que corre en la página.
   *   · Klaviyo — sólo el registro, y sólo después del `identify`: su API de
   *     eventos del navegador descarta a quien todavía no tiene perfil, así que
   *     una vista anónima no se puede contar ahí.
   *
   * Ninguna puede tumbar el popup: todas van envueltas.
   */
  function report(evento, datos) {
    try {
      // Shopify pide prefijo en los eventos personalizados para que no choquen
      // con los suyos ni con los de otra app. El nombre sin prefijo se queda
      // para dataLayer y gtag, que es lo que ya escucha Google Ads.
      window.Shopify?.analytics?.publish(`venner:${evento}`, datos);
    } catch {
      /* Sin web pixels activos no pasa nada. */
    }

    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: evento, ...datos });
    } catch {
      /* Sin dataLayer no pasa nada: el popup no depende de la medición. */
    }

    try {
      if (typeof window.gtag === 'function') window.gtag('event', evento, datos);
    } catch {
      /* Igual: nunca romper el flujo por un error de analítica. */
    }
  }

  /** Añade el cupón a la URL de destino usando la ruta nativa de Shopify. */
  function discountUrl(code, destination) {
    const target = destination && destination !== '#' ? destination : '/collections/all';
    let path = target;

    try {
      // Las URL del theme editor pueden venir absolutas; nos quedamos con la ruta.
      const parsed = new URL(target, window.location.origin);
      path = parsed.pathname + parsed.search + parsed.hash;
    } catch {
      /* Ruta relativa, se usa tal cual. */
    }

    const root = (window.Shopify?.routes?.root || '/').replace(/\/$/, '');
    return `${root}/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(path)}`;
  }

  class VennerScratch extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';

      const configNode = this.querySelector('[data-venner-scratch-config]');
      if (!configNode) return;

      try {
        this.config = JSON.parse(configNode.textContent);
      } catch {
        return;
      }

      if (!this.config.prizes?.length || !this.config.companyId || !this.config.listId) return;

      this.dialog = this.querySelector('.venner-scratch__backdrop');
      this.ticket = this.querySelector('[data-vs-ticket]');
      this.canvas = this.querySelector('[data-vs-canvas]');
      this.emailForm = this.querySelector('[data-vs-step="email"]');
      this.birthdayForm = this.querySelector('[data-vs-birthday]');
      this.steps = Array.from(this.querySelectorAll('[data-vs-step]'));

      this.designMode = Boolean(window.Shopify?.designMode);
      this.scratchedOnce = false;

      this.bind();
      this.preloadCover();

      if (this.designMode) {
        // En el editor el popup lo gobierna la selección de la sección, no las reglas.
        document.addEventListener('shopify:section:select', (event) => {
          if (event.target.contains(this)) this.open();
        });
        document.addEventListener('shopify:section:deselect', (event) => {
          if (event.target.contains(this)) this.close();
        });
        return;
      }

      if (this.shouldSkip()) return;
      this.watchTriggers();
    }

    /* ------------------------------------------------------------ reglas de aparición */

    shouldSkip() {
      if (BOT.test(navigator.userAgent)) return true;
      if (BLOCKED_PATHS.test(window.location.pathname)) return true;

      const saved = read();
      if (!saved) return false;

      // Quien ya se registró no lo vuelve a ver nunca.
      if (saved.s === 'won') return true;

      const wait = days(this.config.frequencyDays || 0);
      return wait > 0 && Date.now() - (saved.ts || 0) < wait;
    }

    watchTriggers() {
      const fire = () => this.open();

      if (this.config.delay >= 0) {
        this.timer = window.setTimeout(fire, (this.config.delay || 0) * 1000);
      }

      if (this.config.scrollPercent > 0) {
        this.onScroll = () => {
          const scrollable = document.documentElement.scrollHeight - window.innerHeight;
          if (scrollable <= 0) return;
          const reached = (window.scrollY / scrollable) * 100 >= this.config.scrollPercent;
          if (reached) fire();
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
      }

      // La intención de salida solo existe con puntero: en móvil no hay «salir por arriba».
      if (this.config.exitIntent && window.matchMedia('(hover: hover)').matches) {
        this.onLeave = (event) => {
          if (event.clientY <= 0) fire();
        };
        document.addEventListener('mouseout', this.onLeave);
      }
    }

    stopTriggers() {
      window.clearTimeout(this.timer);
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      if (this.onLeave) document.removeEventListener('mouseout', this.onLeave);
    }

    /* ------------------------------------------------- convivencia con el aviso de país */

    /**
     * El aviso de país (`sections/venner-geo`) marca `data-state="done"` cuando ya decidió:
     * o no aplicaba, o el visitante lo cerró. Si la sección no está en la página no hay nada
     * que esperar.
     */
    geoPending() {
      if (this.geoWaived) return false;
      const geo = document.querySelector('venner-geo');
      return Boolean(geo) && geo.dataset.state !== 'done';
    }

    deferToGeo() {
      if (this.geoWaiting) return;
      this.geoWaiting = true;

      // Los disparadores ya cumplieron: de aquí en adelante manda el aviso de país.
      this.stopTriggers();

      const resume = () => {
        window.clearTimeout(this.geoTimer);
        document.removeEventListener('venner:geo:settled', resume);
        this.geoWaiting = false;
        // Ya se esperó una vez: no hay que volver a hacerlo en el reintento.
        this.geoWaived = true;
        window.setTimeout(() => this.open(), GEO_GRACE);
      };

      document.addEventListener('venner:geo:settled', resume);

      // Red de seguridad por si el aviso nunca resuelve (su fetch de país se cayó). Si el
      // aviso sí está en pantalla se le sigue esperando: cerrarlo siempre emite la señal.
      this.geoTimer = window.setTimeout(() => {
        if (!document.querySelector('venner-geo[open]')) resume();
      }, GEO_WAIT);
    }

    /* ------------------------------------------------------------ abrir y cerrar */

    open() {
      if (this.hasAttribute('open')) return;

      // El aviso de país va primero: decide a qué tienda pertenece el visitante, y la
      // raspadita encima lo tapaba justo cuando había que contestarlo.
      if (this.geoPending()) {
        this.deferToGeo();
        return;
      }

      this.stopTriggers();

      this.prize = pickPrize(this.config.prizes);
      this.querySelector('[data-vs-prize]').textContent = this.prize.label;

      this.showStep('scratch');
      this.setAttribute('open', '');

      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }

      this.paintCover();

      report('raspadita_vista', { premio: this.prize.label, cupon: this.prize.code });
    }

    close() {
      if (!this.hasAttribute('open')) return;
      this.removeAttribute('open');

      if (this.dialog.open && typeof this.dialog.close === 'function') {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
      }

      if (this.designMode) return;

      // Si ya ganó, el estado «won» ya quedó escrito y no hay que degradarlo.
      const saved = read();
      if (saved?.s !== 'won') {
        write({ s: 'closed', ts: Date.now() });
        report('raspadita_abandonada', {
          premio: this.prize?.label,
          raspo: this.ticket.hasAttribute('data-revealed')
        });
      }
    }

    showStep(name) {
      for (const step of this.steps) {
        step.hidden = step.dataset.vsStep !== name;
      }
    }

    bind() {
      for (const button of this.querySelectorAll('[data-vs-close]')) {
        button.addEventListener('click', () => this.close());
      }

      // El <dialog> ya cierra con Escape; esto mantiene nuestro estado sincronizado.
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });

      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
      });

      const reveal = this.querySelector('[data-vs-reveal]');
      if (reveal) reveal.addEventListener('click', () => this.reveal());

      this.bindCanvas();

      this.emailForm.addEventListener('submit', (event) => {
        event.preventDefault();
        this.subscribe();
      });

      for (const copy of this.querySelectorAll('[data-vs-copy]')) {
        copy.addEventListener('click', () => this.copyCode(copy));
      }

      if (this.birthdayForm) {
        this.birthdayForm.addEventListener('submit', (event) => {
          event.preventDefault();
          this.saveBirthday();
        });
      }

      // Si cambia el ancho antes de raspar hay que volver a dibujar la capa: el canvas
      // se redimensiona y el contenido viejo quedaría estirado.
      window.addEventListener('resize', () => {
        if (!this.hasAttribute('open') || this.scratchedOnce) return;
        if (this.ticket.hasAttribute('data-revealed')) return;
        window.clearTimeout(this.repaintTimer);
        this.repaintTimer = window.setTimeout(() => this.paintCover(), 150);
      });
    }

    /* ------------------------------------------------------------ capa que se raspa */

    /**
     * La imagen de la capa se pide al cargar la página, no al abrir el popup.
     *
     * Medido en 4G lento contra el live: pidiéndola al abrir, la tarjeta se quedaba
     * gris 4.6 s. No es que pese —son 61 kB en WebP, que Shopify negocia solo— es que
     * la petición entraba a competir con el resto de la página justo en ese instante.
     *
     * Va en el hueco libre del navegador para no estorbarle a lo que el visitante sí
     * está mirando, con un tope por si ese hueco nunca llega.
     */
    preloadCover() {
      if (!this.config.coverImage) return;

      const start = () => {
        this.coverImg = new Image();
        this.coverImg.src = this.config.coverImage;
      };

      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(start, { timeout: 3000 });
      } else {
        window.setTimeout(start, 1200);
      }
    }

    paintCover() {
      const canvas = this.canvas;
      const rect = this.ticket.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(rect.width * ratio);
      canvas.height = Math.round(rect.height * ratio);

      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      this.ctx = ctx;
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);

      // El color va siempre primero: la tarjeta nunca debe verse transparente,
      // ni siquiera mientras carga la imagen.
      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = getComputedStyle(this).getPropertyValue('--vs-cover').trim() || '#c9c9c9';
      ctx.fillRect(0, 0, rect.width, rect.height);

      this.paintCoverFinish(rect);

      if (this.config.coverImage) {
        const image = this.coverImg || new Image();

        const draw = () => {
          if (this.ticket.hasAttribute('data-revealed') || this.scratchedOnce) return;
          const scale = Math.max(rect.width / image.width, rect.height / image.height);
          const width = image.width * scale;
          const height = image.height * scale;
          ctx.drawImage(image, (rect.width - width) / 2, (rect.height - height) / 2, width, height);
          this.paintCoverFinish(rect);
        };

        // Si la precarga ya terminó, se dibuja en el mismo cuadro y nadie ve el gris.
        if (image.complete && image.naturalWidth) {
          draw();
        } else {
          image.onload = draw;
          if (!image.src) image.src = this.config.coverImage;
        }
      }
    }

    /**
     * El velo y el texto, en ese orden. Van juntos a propósito: el velo sólo
     * sirve para que el texto se lea encima de la foto, y la imagen se dibuja
     * de forma asíncrona, así que separarlos acaba pintando el texto debajo.
     */
    paintCoverFinish(rect) {
      const fuerza = (this.config.coverTintStrength || 0) / 100;
      const tinte = this.config.coverTint;

      if (fuerza > 0 && (tinte === 'dark' || tinte === 'light')) {
        const ctx = this.ctx;
        ctx.save();
        ctx.globalCompositeOperation = 'source-over';
        ctx.fillStyle = tinte === 'dark' ? `rgb(0 0 0 / ${fuerza})` : `rgb(255 255 255 / ${fuerza})`;
        ctx.fillRect(0, 0, rect.width, rect.height);
        ctx.restore();
      }

      this.paintCoverText(rect);
    }

    paintCoverText(rect) {
      const text = this.config.coverText;
      if (!text) return;

      const ctx = this.ctx;
      ctx.save();
      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = getComputedStyle(this).getPropertyValue('--vs-cover-text').trim() || '#6b6b6b';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      if ('letterSpacing' in ctx) ctx.letterSpacing = '3px';
      ctx.font = `600 ${Math.max(13, Math.round(rect.width * 0.055))}px ${
        getComputedStyle(this).fontFamily || 'sans-serif'
      }`;
      ctx.fillText(text, rect.width / 2, rect.height / 2);
      ctx.restore();
    }

    bindCanvas() {
      const canvas = this.canvas;
      let drawing = false;
      let last = null;
      let checkedAt = 0;

      const pointAt = (event) => {
        const rect = canvas.getBoundingClientRect();
        return { x: event.clientX - rect.left, y: event.clientY - rect.top };
      };

      const scratch = (from, to) => {
        const ctx = this.ctx;
        if (!ctx) return;

        ctx.globalCompositeOperation = 'destination-out';
        ctx.lineWidth = this.config.brushSize || 34;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.beginPath();
        ctx.moveTo(from.x, from.y);
        ctx.lineTo(to.x, to.y);
        ctx.stroke();

        // Un punto suelto (un toque sin arrastrar) no deja traza con lineTo.
        ctx.beginPath();
        ctx.arc(to.x, to.y, (this.config.brushSize || 34) / 2, 0, Math.PI * 2);
        ctx.fill();
      };

      canvas.addEventListener('pointerdown', (event) => {
        if (this.ticket.hasAttribute('data-revealed')) return;
        drawing = true;
        this.scratchedOnce = true;

        // Capturar el puntero evita perder el trazo si el dedo se sale de la tarjeta.
        // No todos los navegadores lo permiten para cualquier puntero, y fallar aquí
        // no debe impedir raspar.
        try {
          canvas.setPointerCapture(event.pointerId);
        } catch {
          /* Seguimos sin captura: el `pointerleave` cierra el trazo. */
        }

        last = pointAt(event);
        scratch(last, last);
      });

      canvas.addEventListener('pointermove', (event) => {
        if (!drawing) return;
        event.preventDefault();
        const next = pointAt(event);
        scratch(last || next, next);
        last = next;

        // Leer los píxeles es caro: una vez cada 150 ms es suficiente.
        const now = Date.now();
        if (now - checkedAt > 150) {
          checkedAt = now;
          this.checkProgress();
        }
      });

      const stop = () => {
        if (!drawing) return;
        drawing = false;
        last = null;
        this.checkProgress();
      };

      canvas.addEventListener('pointerup', stop);
      canvas.addEventListener('pointercancel', stop);
      canvas.addEventListener('pointerleave', stop);
    }

    /** Porcentaje de la capa ya raspado, muestreando la rejilla en pasos de 8 px. */
    checkProgress() {
      const ctx = this.ctx;
      if (!ctx || this.ticket.hasAttribute('data-revealed')) return;

      const { width, height } = this.canvas;
      const pixels = ctx.getImageData(0, 0, width, height).data;
      const stride = 8;

      let clear = 0;
      let total = 0;

      for (let y = 0; y < height; y += stride) {
        for (let x = 0; x < width; x += stride) {
          total += 1;
          if (pixels[(y * width + x) * 4 + 3] < 32) clear += 1;
        }
      }

      if (total && (clear / total) * 100 >= (this.config.threshold || 55)) this.reveal();
    }

    reveal() {
      if (this.ticket.hasAttribute('data-revealed')) return;
      this.ticket.setAttribute('data-revealed', '');

      const hint = this.querySelector('[data-vs-hint]');
      if (hint) hint.hidden = true;

      report('raspadita_raspada', { premio: this.prize.label, cupon: this.prize.code });

      const motion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.setTimeout(() => this.toEmailStep(), motion ? 0 : 500);
    }

    toEmailStep() {
      const won = this.querySelector('[data-vs-won]');
      if (won) won.textContent = this.prize.label;

      const values = { prize: this.prize.label, code: this.prize.code };
      for (const node of this.querySelectorAll('[data-vs-step2-text]')) {
        node.innerHTML = fill(node.dataset.vsTemplate || (node.dataset.vsTemplate = node.innerHTML), values);
      }

      this.showStep('email');

      // En móvil no enfocamos solo: abrir el teclado de golpe tapa el premio.
      if (window.matchMedia('(hover: hover)').matches) {
        this.querySelector('[data-vs-email]')?.focus();
      }
    }

    /* ------------------------------------------------------------ Klaviyo */

    klaviyoUrl(path) {
      return `${KLAVIYO_HOST}${path}?company_id=${encodeURIComponent(this.config.companyId)}`;
    }

    klaviyoHeaders() {
      return {
        'content-type': 'application/vnd.api+json',
        revision: this.config.revision
      };
    }

    async subscribe() {
      const input = this.querySelector('[data-vs-email]');
      const error = this.querySelector('[data-vs-error]');
      const button = this.querySelector('[data-vs-submit]');
      const label = this.querySelector('[data-vs-submit-label]');
      const email = input.value.trim();

      const showError = (message) => {
        error.textContent = message;
        error.hidden = false;
        input.setAttribute('aria-invalid', 'true');
      };

      if (!input.checkValidity() || !email) {
        showError(this.config.errorEmail);
        input.focus();
        return;
      }

      error.hidden = true;
      input.removeAttribute('aria-invalid');
      button.disabled = true;
      const original = label.textContent;
      if (this.config.sendingText) label.textContent = this.config.sendingText;

      const payload = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: this.config.source,
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email,
                  properties: {
                    premio_raspadita: this.prize.label,
                    cupon_raspadita: this.prize.code,
                    fuente_registro: this.config.source
                  },
                  subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
                }
              }
            }
          },
          relationships: {
            list: { data: { type: 'list', id: this.config.listId } }
          }
        }
      };

      try {
        const response = await fetch(this.klaviyoUrl('/client/subscriptions'), {
          method: 'POST',
          headers: this.klaviyoHeaders(),
          body: JSON.stringify(payload)
        });

        if (!response.ok) throw new Error(`Klaviyo respondió ${response.status}`);

        this.email = email;
        write({ s: 'won', code: this.prize.code, label: this.prize.label, ts: Date.now() });

        // Amarra la sesión del navegador al perfil para no perder la atribución.
        // El `track` va después del `identify` a propósito: Klaviyo descarta los
        // eventos de quien todavía no tiene perfil.
        try {
          window.klaviyo?.push(['identify', { $email: email }]);
          window.klaviyo?.push([
            'track',
            'Raspadita registro',
            { Premio: this.prize.label, Cupon: this.prize.code, Origen: this.config.source }
          ]);
        } catch {
          /* El embed de Klaviyo puede no haber cargado todavía. */
        }

        report('raspadita_registro', { premio: this.prize.label, cupon: this.prize.code });

        this.toCodeStep();
      } catch {
        showError(this.config.errorNetwork);
        button.disabled = false;
        label.textContent = original;
      }
    }

    toCodeStep() {
      const values = { prize: this.prize.label, code: this.prize.code };

      for (const node of this.querySelectorAll('[data-vs-success-text]')) {
        node.innerHTML = fill(node.dataset.vsTemplate || (node.dataset.vsTemplate = node.innerHTML), values);
      }

      this.querySelector('[data-vs-code]').textContent = this.prize.code;

      const note = this.querySelector('[data-vs-note]');
      if (note && this.prize.note) {
        note.textContent = this.prize.note;
        note.hidden = false;
      }

      const href = discountUrl(this.prize.code, this.prize.applyUrl);
      for (const link of this.querySelectorAll('[data-vs-apply]')) {
        link.href = href;
      }

      this.showStep('code');
    }

    async copyCode(button) {
      const hint = button.querySelector('[data-vs-copy-hint]');
      try {
        await navigator.clipboard.writeText(this.prize.code);
      } catch {
        // Safari en contextos sin permiso: seleccionar el texto ya permite copiar a mano.
        const range = document.createRange();
        range.selectNodeContents(button.querySelector('[data-vs-code]'));
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
        return;
      }

      if (!hint || !this.config.copiedText) return;
      const original = hint.textContent;
      hint.textContent = this.config.copiedText;
      window.setTimeout(() => {
        hint.textContent = original;
      }, 2000);
    }

    /* ------------------------------------------------------------ cumpleaños */

    async saveBirthday() {
      const day = Number(this.querySelector('[data-vs-day]').value);
      const month = Number(this.querySelector('[data-vs-month]').value);
      const error = this.querySelector('[data-vs-birthday-error]');

      if (!day || !month) {
        error.textContent = this.config.birthdayError;
        error.hidden = false;
        return;
      }

      error.hidden = true;

      /*
        Klaviyo necesita una fecha completa para que el flow por propiedad de fecha
        dispare, pero solo compara día y mes. De ahí el año de relleno del schema:
        no preguntamos el año real para no pedir un dato que no usamos.
      */
      const padded = (value) => String(value).padStart(2, '0');
      const birthday = `${this.config.birthdayYear}-${padded(month)}-${padded(day)}`;

      // Para el pedido va en legible, con el nombre de mes que esté configurado.
      const selectMes = this.querySelector('[data-vs-month]');
      // En minúscula: el nombre viene de la etiqueta del desplegable, donde va
      // capitalizado, pero a media frase el español lo quiere en minúscula.
      const nombreMes = (selectMes.selectedOptions[0]?.textContent.trim() || String(month)).toLowerCase();
      const texto = `${day} de ${nombreMes}`;

      const payload = {
        data: {
          type: 'profile',
          attributes: {
            email: this.email,
            properties: {
              cumpleanos: birthday,
              cumple_dia: day,
              cumple_mes: month
            }
          }
        }
      };

      try {
        await fetch(this.klaviyoUrl('/client/profiles'), {
          method: 'POST',
          headers: this.klaviyoHeaders(),
          body: JSON.stringify(payload)
        });
      } catch {
        /* El correo ya está capturado: un fallo aquí no debe frenar al cliente. */
      }

      /*
        Copia opcional al carrito, para que el cumpleaños se vea en el pedido.
        Va después de Klaviyo y aislada: Klaviyo es el registro bueno, esto es
        una comodidad para el admin y no debe poder tumbarla.

        Alcance limitado a propósito: el atributo vive en el carrito de ESTE
        navegador, así que sólo llega al pedido de quien compra sin cambiar de
        dispositivo ni borrar cookies.
      */
      if (this.config.orderAttribute) {
        try {
          const root = (window.Shopify?.routes?.root || '/').replace(/\/$/, '');
          await fetch(`${root}/cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attributes: { [this.config.orderAttribute]: texto } })
          });
        } catch {
          /* El cumpleaños ya quedó en Klaviyo; esto es un extra. */
        }
      }

      this.birthdayForm.hidden = true;

      const done = this.querySelector('[data-vs-birthday-done]');
      if (done) done.hidden = false;

      report('raspadita_cumpleanos', { mes: month });
    }
  }

  if (!window.customElements.get('venner-scratch')) {
    window.customElements.define('venner-scratch', VennerScratch);
  }
})();
